import { SearchService } from './../search.service';
import {
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  OnDestroy,
  Output,
  OnInit,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ModalService } from 'src/app/shared/services/modal.service';
import { SearchOverlayService } from 'src/app/shared/services/search-overlay.service';
import {
  ActionIcons,
  basicSearchInputRegex,
  basicSearchShortcutKeys,
  BasicSearchValues,
  keyStrokes,
  ModalPopupMessages,
  pageType,
  pageUrl,
  popupWidth,
  searchDataParam,
} from 'src/app/shared/utility/constants';
import {
  BasicSearchIcons,
  BasicSearchInputIcons,
} from 'src/app/shared/utility/svg-constants';
import { SearchPopupComponent } from '../search-options/search-popup/search-popup.component';
import { FormControl } from '@angular/forms';
import { CommonService } from 'src/app/shared/services/common.service';
import { Router } from '@angular/router';

export interface BasicSearchClickedEvent {
  data: string;
  shipmentId?: unknown;
}

@Component({
  selector: 'app-basic-search',
  templateUrl: './basic-search.component.html',
  styleUrl: './basic-search.component.scss',
})
export class BasicSearchComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);
  private readonly hostElement: ElementRef<HTMLElement> = inject(ElementRef);
  @ViewChild('searchOverlay', { read: ElementRef })
  private readonly searchOverlayAnchor?: ElementRef<HTMLElement>;
  @Input() placeholder!: string;
  @Input() isFrom!: string;
  @Input() helpTextTooltip!: string;
  @Input() hideSearchOptions!: boolean;
  @Output() isSearchClicked = new EventEmitter<BasicSearchClickedEvent>();
  @Output() isSearchFieldHasValue = new EventEmitter<boolean>(false);
  @Output() isAdvanceSearchClicked = new EventEmitter<boolean>(false);
  public searchOptions = BasicSearchIcons;
  public inputSearchOptions = BasicSearchInputIcons;
  public searchIconTooltip = ActionIcons.search;
  public infoIconTooltip!: string;
  public basicSearch = new FormControl('');
  public searchIcon = ActionIcons.searchSmallCase;
  public hamburgerIcon = ActionIcons.hamburger;
  public isPageConstant = searchDataParam.basicSearch;

  private readonly isLimitReached = false;
  private clickedHyperLinkedId: any;

  constructor(
    public searchService: SearchService,
    private readonly overlayService: SearchOverlayService,
    private readonly modalService: ModalService,
    private readonly commonService: CommonService,
    private readonly router: Router
  ) {}

  // Read from the service so summary pages see the overlay as closed after an
  // outside click.
  public get isOverlayOpen(): boolean {
    return this.overlayService.isOverlayOpen;
  }

  ngOnInit(): void {
    this.getHyperlinkClickedDataForSearch();
    this.getMasterShipmentClickedDataForSearch();
    this.infoIconTooltip = this.helpTextTooltip;
    this.setPatchValue();
    const searchValue: any = this.commonService.getLastUsedPayload();
    if (searchValue) {
      this.basicSearch.setValue(searchValue.searchText);
    }
  }

  ngOnDestroy(): void {
    this.overlayService.closeOverlay();
  }

  // SPEC-BUX-001 FR-BUX-009/010/014/015: summary-page shortcuts for Advanced
  // Search and the Recent / Pinned overlay.
  // Esc closing the Recent / Pinned menu lives in SearchOverlayService (see toggleOverlay).
  @HostListener('document:keydown', ['$event'])
  public onDocumentKeydown(event: KeyboardEvent): void {
    if (event.key === keyStrokes.escape) {
      return;
    }
    if (!this.areSearchShortcutsEnabled()) {
      return;
    }
    if (!event.shiftKey || !event.altKey || event.ctrlKey || event.metaKey) {
      return;
    }
    if (this.isShortcut(event, basicSearchShortcutKeys.advancedSearch)) {
      event.preventDefault();
      this.onSearchOptionsIconClicked(
        { name: ActionIcons.advanceSearch },
        this.searchOverlayAnchor
      );
    } else if (
      this.isShortcut(event, basicSearchShortcutKeys.recentAndPinnedSearches)
    ) {
      event.preventDefault();
      this.toggleOverlay(this.searchOverlayAnchor, true);
      if (!this.isOverlayOpen) {
        this.focusHamburgerIcon();
      }
    }
  }

  private areSearchShortcutsEnabled(): boolean {
    return (
      this.isSearchShortcutPage() &&
      !this.commonService.checkOverlayOpened.value
    );
  }

  private isSearchShortcutPage(): boolean {
    return (
      !this.hideSearchOptions &&
      [pageType.hawb, pageType.bag, pageType.manifest, pageType.mawb].includes(
        this.isFrom
      )
    );
  }

  private isShortcut(
    event: KeyboardEvent,
    shortcut: { key: string; code: string }
  ): boolean {
    return (
      event.key.toLowerCase() === shortcut.key || event.code === shortcut.code
    );
  }

  private focusBasicSearchInput(): void {
    this.hostElement.nativeElement
      .querySelector<HTMLInputElement>('.basic-search input')
      ?.focus();
  }

  private focusHamburgerIcon(): void {
    this.hostElement.nativeElement
      .querySelector<HTMLElement>(`.search-option-icons.${this.hamburgerIcon}`)
      ?.focus();
  }

  public onInput(event: any): void {
    if (
      [
        pageType.hawb,
        pageType.bag,
        pageType.manifest,
        pageType.mawb,
      ].includes(this.isFrom)
    ) {
      const inputValue = event.target.value;
      const sanitizedValue = inputValue.replace(
        basicSearchInputRegex.mainModules,
        ''
      );
      this.basicSearch.setValue(sanitizedValue);
    } else if (
      [
        pageType.account,
        pageType.rolesAndPermissions,
        pageType.smStatusCodes,
        pageType.smNumberRanges,
        pageType.serviceTypes,
        pageType.rateScales,
        pageType.apChargeCodes,
      ].includes(this.isFrom)
    ) {
      const inputValue = event.target.value;
      const sanitizedValue = inputValue.replace(
        basicSearchInputRegex.smModules,
        ''
      );
      this.basicSearch.setValue(sanitizedValue);
    } else if (pageType.usersManagement) {
      const inputValue = event.target.value;
      const sanitizedValue = inputValue.replace(
        basicSearchInputRegex.usersModule,
        ''
      );
      this.basicSearch.setValue(sanitizedValue);
    }
  }

  public search(isFromComp: boolean) {
    if (this.basicSearch.value) {
      this.commonService.isListAllResultClicked(false);
      this.clickedHyperLinkedId = !isFromComp
        ? undefined
        : this.clickedHyperLinkedId;
      this.isSearchClicked.emit({
        data: this.basicSearch.value.trim(),
        shipmentId: this.clickedHyperLinkedId,
      });
    }
  }

  public getPinIconStatus(options: any) {
    return (
      options.name === 'pin' && (this.isLimitReached || !this.basicSearch.value)
    );
  }

  public onSearchOptionsIconClicked(clickedIcon: any, searchOverlay: any) {
    if (clickedIcon.name === ActionIcons.pinSearch) {
      this.pinSearchPopup();
    } else if (clickedIcon.name === ActionIcons.advanceSearch) {
      if (this.isFrom === pageType.hawb) {
        this.isAdvanceSearchClicked.emit(true);
        this.router.navigate([pageUrl.hawbAdvancedSearchPage]);
      }
      if (this.isFrom === pageType.bag) {
        this.isAdvanceSearchClicked.emit(true);
        this.router.navigate([pageUrl.bagAdvancedSearchPage]);
      }
      if (this.isFrom === pageType.mawb) {
        this.isAdvanceSearchClicked.emit(true);
        this.router.navigate([pageUrl.mawbAdvancedSearchPage]);
      }
      if (this.isFrom === pageType.manifest) {
        this.isAdvanceSearchClicked.emit(true);
        this.router.navigate([pageUrl.manifestAdvancedSearchPage]);
      }
    } else if (clickedIcon.name === ActionIcons.hamburger) {
      this.toggleOverlay(searchOverlay);
    }
  }

  public getTooltipDisplayName(optionName: string): string {
    const displayNames: { [key: string]: string } = {
      pin: BasicSearchValues.pin,
      advancedSearch: BasicSearchValues.advancedSearch,
      hamburger: BasicSearchValues.hamburger,
    };
    const shortcutLabels: { [key: string]: string } = {
      advancedSearch: basicSearchShortcutKeys.advancedSearch.label,
      hamburger: basicSearchShortcutKeys.recentAndPinnedSearches.label,
    };
    const displayName = displayNames[optionName] || optionName;
    // SPEC-BUX-001 FR-BUX-025: show the shortcut next to the icon name.
    return shortcutLabels[optionName] && this.isSearchShortcutPage()
      ? `${displayName} (${shortcutLabels[optionName]})`
      : displayName;
  }

  public onListAllResultsClicked() {
    this.commonService.isListAllResultClicked(true);
    this.basicSearch.setValue('');
    this.isSearchClicked.emit({
      data: '',
    });
  }

  private pinSearchPopup() {
    const dataForPopup = {
      data: {
        searchName: this.basicSearch.value,
        description: '',
        searchId: 0,
        isFrom: this.isFrom,
        isBasicSearch: true,
      },
    };
    this.modalService.openPopup(
      dataForPopup,
      ModalPopupMessages.pinSearch,
      SearchPopupComponent,
      popupWidth.searchPopup
    );
  }

  private toggleOverlay(searchOverlay?: any, focusFirstItem = false) {
    if (this.overlayService.isOverlayOpen) {
      this.overlayService.closeOverlay();
    } else {
      this.overlayService.openSearchOverlay(
        searchOverlay,
        this.isFrom,
        focusFirstItem,
        () => this.focusBasicSearchInput()
      );
    }
  }

  private getHyperlinkClickedDataForSearch() {
    this.searchService.clickedHyperlinkData
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(clickedData => {
        if (clickedData) {
          this.overlayService.closeOverlay();
          this.basicSearch.setValue(clickedData.item?.savedSearchName);
          this.search(true);
        }
      });
  }

  private getMasterShipmentClickedDataForSearch() {
    this.commonService.isSetMasterShipmentValue
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(shipmentData => {
        if (Object.keys(shipmentData).length) {
          this.basicSearch.setValue(shipmentData.shipmentExt);
          this.clickedHyperLinkedId = shipmentData.shipmentId;
          this.search(true);
        }
      });
  }

  private setPatchValue() {
    this.commonService.patchSearchText
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(patchData => {
        if (patchData) {
          this.basicSearch.setValue(patchData);
          setTimeout(() => {
            this.commonService.setSearchText('');
          }, 50);
        }
      });
  }
}
